import { PermitStatus } from './entities/permit/PermitBase'
import { Profile } from './entities/profile'
import { ObservationCaptureMode } from './entities/observation/types'

export const userProperties = (profile: Profile) => {
  const organisations = profile?.organisations || []
  const projects = profile?.projects || []

  return {
    userId: `${profile.id}`,
    properties: {
      name: `${profile?.firstName} ${profile?.lastName}`,
      email: profile?.email,
      organisationIds: organisations.map((o) => o.id),
      organisationNames: organisations.map((o) => o.name),
      organisations: organisations,
      projectIds: projects.map((p) => p.id),
      projectNames: projects.map((p) => p.name),
      projects: projects,
    },
  }
}

export type HelpOpened = {
  eventName: 'help.opened'
  eventProperties?: null
}

export type TestingEventWithProperties = {
  eventName: 'testing.event'
  eventProperties: {
    something: string
  }
}

export type TestingEventWithoutProperties = {
  eventName: 'testing.event.without.properties'
  eventProperties?: null
}

export type MobileCodePushVersionLaunched = {
  eventName: 'mobile.codepush-version.launched'
  eventProperties: {
    environment: string
    version: string
    storageUsedBytes: number
  }
}

export type MobileCheckinLocationError = {
  eventName: 'mobile.check-in.location.error'
  eventProperties: {
    errorCode: number
    errorMessage: string
  }
}

export type MobilePermitDetailsNavigationMethod = {
  eventName: 'mobile.ux.permit-details.navigation-method'
  eventProperties: {
    method: 'app-bar' | 'hyperlink-text' | 'summary-section-card'
    route: string
  }
}

export type MobileOfflineActionCached = {
  eventName: 'mobile.offline-action-cached'
  eventProperties?: {}
}

export type MobileOfflineActionSynced = {
  eventName: 'mobile.offline-action-synced'
  eventProperties?: { isBackgroundSync: boolean }
}

export type MobileOfflineActionSyncFailed = {
  eventName: 'mobile.offline-action-sync-failed'
  eventProperties?: { isBackgroundSync: boolean }
}

export type MobileNavigationEvent = {
  eventName: 'mobile.navigation'
  eventProperties: {
    route: string
  }
}

export type BackgroundSyncTimeout = {
  eventName: 'mobile.offline-action-sync-timeout'
  eventProperties?: {}
}

export type PusherConnectionEvent = {
  eventName: 'pusher.connection-successful' | 'pusher.connection-closed'
  eventProperties: {
    platform: 'web' | 'mobile'
  }
}

export type PusherSubscriptionEvent = {
  eventName: 'pusher.subscription-successful' | 'pusher.subscription-closed'
  eventProperties: {
    platform: 'web' | 'mobile'
    channel: string
  }
}

export type MobileProjectPickerChanged = {
  eventName: 'mobile.project-picker.changed'
  eventProperties: {
    newProjectId: number
  }
}

export type MobileApproverNominationSkipped = {
  eventName: 'mobile.approver-nomination.skipped'
  eventProperties?: null
}

export type MobileApproverNominationConfirmed = {
  eventName: 'mobile.approver-nomination.confirmed'
  eventProperties: {
    approverId: number
  }
}

export type PinsTutorialCompleted = {
  eventName: 'tutorial.completed'
  eventProperties: {
    platform: 'web' | 'mobile'
    tutorial: 'pins'
    skipped: boolean
  }
}

export type ProjectDashboardPermitClick = {
  eventName: 'project.dashboard.permit.click'
  eventProperties: {
    type: 'status' | 'date'
    month: string
    status?: PermitStatus
  }
}

export type ProjectDashboardInspectionClick = {
  eventName: 'project.dashboard.inspection.click'
  eventProperties: {
    type: 'openTasks' | 'overdueTasks' | 'completedInspections'
    month: string
  }
}

export type NotificationListOpened = {
  eventName: 'notification.list.opened'
  eventProperties: {
    platform: 'web' | 'mobile'
    numberOfUnseenNotifications: number
    numberOfUnreadNotifications: number
  }
}

export type NotificationInteraction = {
  eventName: 'notification.interaction'
  eventProperties: {
    platform: 'web' | 'mobile'
    notificationType: string
    alreadyRead: boolean
  }
}

export type NotificationMarkAllAsRead = {
  eventName: 'notification.mark-all-as-read'
  eventProperties: {
    platform: 'web' | 'mobile'
  }
}

export type FilterEvent = {
  eventName: 'filter'
  eventProperties: {
    view: string
    option: string
    value: any
  }
}

export type KioskNavigation = {
  eventName: 'kiosk.navigation'
  eventProperties: {
    from: string
    to: string
  }
}

export type KioskSession = {
  eventName: 'kiosk.session'
  eventProperties: {
    durationSeconds: number
    pageCount: number
    pages: string[]
    startPage: string
    endPage: string
    endReason: 'return_to_home' | 'inactivity'
  }
}

export type ObservationVideoSource = 'in-app-capture' | 'camera-roll'

export type ObservationsCreated = {
  eventName: 'observations.created'
  eventProperties: {
    captureMode: Lowercase<ObservationCaptureMode>
    videoSource?: ObservationVideoSource
  }
}

export type AnalyticsEvent =
  | TestingEventWithProperties
  | TestingEventWithoutProperties
  | HelpOpened
  | MobileCodePushVersionLaunched
  | MobileCheckinLocationError
  | MobilePermitDetailsNavigationMethod
  | MobileOfflineActionCached
  | MobileOfflineActionSynced
  | MobileOfflineActionSyncFailed
  | MobileNavigationEvent
  | BackgroundSyncTimeout
  | PusherConnectionEvent
  | PusherSubscriptionEvent
  | MobileProjectPickerChanged
  | MobileApproverNominationSkipped
  | MobileApproverNominationConfirmed
  | PinsTutorialCompleted
  | ProjectDashboardPermitClick
  | ProjectDashboardInspectionClick
  | NotificationListOpened
  | NotificationInteraction
  | NotificationMarkAllAsRead
  | FilterEvent
  | KioskNavigation
  | KioskSession
  | ObservationsCreated
